import {
  booleanAttr,
  NOTICE_STYLES,
  PAGE_BREAK_MARKUP,
  rawAttr,
  stringAttr,
  type NoticeStyle,
} from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { TextSelection } from "prosemirror-state";
import type { EditorView, NodeView, ViewMutationRecord } from "prosemirror-view";

import { CODE_LANGUAGES } from "../commands/code";
import type { EditorContext } from "../context";
import { isToggleOpen, setToggleOpen, subscribeToggleState, toggleKey } from "../toggle-state";

function toolbarButton(label: string, title: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = "wiki-node-toolbar__button";
  element.textContent = label;
  element.title = title;
  element.setAttribute("aria-label", title);
  element.addEventListener("click", (event) => {
    event.preventDefault();
    onClick();
  });
  return element;
}

/**
 * Code blocks: the language picker, the wrap toggle, a copy button, and line
 * numbers when the user preference asks for them (`editor.md` §5). The picker
 * keeps a language it does not know, because the dialect preserves unknown
 * languages verbatim.
 */
export class CodeBlockView implements NodeView {
  readonly dom: HTMLElement;
  readonly contentDOM: HTMLElement;
  private node: ProseMirrorNode;
  private readonly header: HTMLElement;
  private readonly gutter: HTMLElement;
  private readonly picker: HTMLSelectElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("div");
    this.dom.className = "wiki-code";
    this.header = document.createElement("div");
    this.header.className = "wiki-code__header";
    this.gutter = document.createElement("div");
    this.gutter.className = "wiki-code__gutter";
    this.gutter.setAttribute("aria-hidden", "true");
    const pre = document.createElement("pre");
    const code = document.createElement("code");
    pre.append(code);
    this.contentDOM = code;
    this.picker = document.createElement("select");
    this.picker.className = "wiki-code__language";
    this.picker.setAttribute("aria-label", "Code language");
    this.picker.addEventListener("change", () => {
      const pos = this.getPos();
      if (pos === undefined) return;
      this.view.dispatch(this.view.state.tr.setNodeAttribute(pos, "language", this.picker.value));
    });
    this.dom.append(this.header, this.gutter, pre);
    this.render();
  }

  private language(): string {
    const language = rawAttr(this.node, "language");
    return typeof language === "string" ? language : "";
  }

  private render(): void {
    const language = this.language();
    const wrap = booleanAttr(this.node, "wrap");
    this.dom.setAttribute("data-language", language);
    this.dom.classList.toggle("wiki-code--wrap", wrap);
    this.contentDOM.className = language === "" ? "" : `language-${language}`;

    const options = CODE_LANGUAGES.some((entry) => entry.value === language)
      ? CODE_LANGUAGES
      : [...CODE_LANGUAGES, { value: language, label: language }];
    if (this.picker.options.length !== options.length) {
      this.picker.replaceChildren();
      for (const entry of options) {
        const option = document.createElement("option");
        option.value = entry.value;
        option.textContent = entry.label;
        this.picker.append(option);
      }
    }
    this.picker.value = language;

    this.header.replaceChildren();
    // Read-only keeps the copy button and hides the picker (§15).
    if (!this.context.readOnly) {
      this.header.append(
        this.picker,
        toolbarButton(wrap ? "Unwrap" : "Wrap text", "Toggle text wrapping", () => {
          const pos = this.getPos();
          if (pos === undefined) return;
          this.view.dispatch(this.view.state.tr.setNodeAttribute(pos, "wrap", !wrap));
        }),
      );
    }
    this.header.append(
      toolbarButton("Copy", "Copy code", () => {
        void navigator.clipboard.writeText(this.node.textContent);
      }),
    );

    this.gutter.replaceChildren();
    if (this.context.lineNumbers) {
      const lines = this.node.textContent.split("\n").length;
      for (let line = 1; line <= lines; line += 1) {
        const element = document.createElement("span");
        element.textContent = String(line);
        this.gutter.append(element);
      }
    }
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target instanceof HTMLElement && this.header.contains(event.target);
  }

  ignoreMutation(mutation: ViewMutationRecord): boolean {
    return !this.contentDOM.contains(mutation.target);
  }
}

/** The notice style switcher (`editor.md` §5, Notice). */
export class NoticeView implements NodeView {
  readonly dom: HTMLElement;
  readonly contentDOM: HTMLElement;
  private node: ProseMirrorNode;
  private readonly switcher: HTMLElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("div");
    this.switcher = document.createElement("div");
    this.switcher.className = "wiki-node-toolbar wiki-notice__styles";
    this.contentDOM = document.createElement("div");
    this.contentDOM.className = "wiki-notice__content";
    this.dom.append(this.switcher, this.contentDOM);
    this.render();
  }

  private render(): void {
    const style = stringAttr(this.node, "style") ?? "info";
    this.dom.className = `wiki-notice wiki-notice--${style}`;
    this.dom.setAttribute("data-notice", style);
    this.switcher.replaceChildren();
    if (this.context.readOnly) return;
    for (const option of NOTICE_STYLES) {
      const label = `${option.charAt(0).toUpperCase()}${option.slice(1)} notice`;
      const element = toolbarButton(label, label, () => {
        const pos = this.getPos();
        if (pos === undefined) return;
        this.view.dispatch(this.view.state.tr.setNodeAttribute(pos, "style", option));
      });
      element.setAttribute("data-notice-style", option);
      if (option === (style as NoticeStyle)) element.setAttribute("aria-pressed", "true");
      this.switcher.append(element);
    }
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target instanceof HTMLElement && this.switcher.contains(event.target);
  }

  ignoreMutation(mutation: ViewMutationRecord): boolean {
    return !this.contentDOM.contains(mutation.target);
  }
}

/**
 * Toggle blocks. Open/closed is per user and per browser, never in the
 * document, so the view reads it from the toggle store and re-renders when any
 * toggle changes.
 */
export class ToggleView implements NodeView {
  readonly dom: HTMLElement;
  readonly contentDOM: HTMLElement;
  private node: ProseMirrorNode;
  private readonly chevron: HTMLButtonElement;
  private readonly unsubscribe: () => void;

  constructor(
    node: ProseMirrorNode,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("div");
    this.contentDOM = document.createElement("div");
    this.contentDOM.className = "wiki-toggle__content";
    this.chevron = document.createElement("button");
    this.chevron.type = "button";
    this.chevron.className = "wiki-toggle__chevron";
    this.chevron.addEventListener("click", (event) => {
      event.preventDefault();
      const key = toggleKey(this.node);
      setToggleOpen(this.context.documentId, key, !isToggleOpen(this.context.documentId, key));
    });
    this.dom.append(this.chevron, this.contentDOM);
    this.unsubscribe = subscribeToggleState(() => {
      this.render();
    });
    this.render();
  }

  private render(): void {
    const open = isToggleOpen(this.context.documentId, toggleKey(this.node));
    this.dom.className = `wiki-toggle ${open ? "wiki-toggle--open" : "wiki-toggle--closed"}`;
    this.dom.setAttribute("data-toggle-key", toggleKey(this.node));
    this.chevron.textContent = open ? "▾" : "▸";
    this.chevron.setAttribute("aria-expanded", open ? "true" : "false");
    this.chevron.setAttribute("aria-label", open ? "Collapse" : "Expand");
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target === this.chevron;
  }

  ignoreMutation(mutation: ViewMutationRecord): boolean {
    return !this.contentDOM.contains(mutation.target);
  }

  destroy(): void {
    this.unsubscribe();
  }
}

type KatexModule = typeof import("katex");

let katexModule: Promise<KatexModule> | null = null;

/**
 * KaTeX is a third of the editor's weight and most documents hold no maths, so
 * it is imported on demand: the first math node on a page pulls the chunk and
 * every later one reuses it.
 */
function loadKatex(): Promise<KatexModule> {
  katexModule ??= import("katex");
  return katexModule;
}

/** Render LaTeX into an element, falling back to its source on any failure. */
export function renderMath(
  target: HTMLElement,
  latex: string,
  displayMode: boolean,
): Promise<void> {
  target.textContent = latex;
  return loadKatex()
    .then((module) => {
      module.default.render(latex, target, { displayMode, throwOnError: false, output: "html" });
    })
    .catch(() => {
      target.textContent = latex;
    });
}

/**
 * Math nodes render through KaTeX and fall back to their source while the
 * caret is inside them, which is how they are edited (`editor.md` §5).
 */
export class MathView implements NodeView {
  readonly dom: HTMLElement;
  private node: ProseMirrorNode;
  private readonly rendered: HTMLElement;
  private readonly source: HTMLTextAreaElement;
  private editing = false;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
    private readonly displayMode: boolean,
  ) {
    this.node = node;
    this.dom = document.createElement(displayMode ? "div" : "span");
    this.dom.className = displayMode ? "wiki-math wiki-math--block" : "wiki-math wiki-math--inline";
    this.rendered = document.createElement(displayMode ? "div" : "span");
    this.rendered.className = "wiki-math__rendered";
    this.source = document.createElement("textarea");
    this.source.className = "wiki-math__source";
    this.source.rows = 1;
    this.source.addEventListener("input", () => {
      this.commit(this.source.value);
    });
    this.source.addEventListener("blur", () => {
      this.editing = false;
      this.render();
    });
    this.rendered.addEventListener("click", () => {
      if (this.context.readOnly) return;
      this.editing = true;
      this.render();
      this.source.focus();
    });
    this.dom.append(this.rendered, this.source);
    this.render();
  }

  private commit(latex: string): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    const schema = this.view.state.schema;
    const tr = this.view.state.tr.replaceWith(
      pos + 1,
      pos + this.node.nodeSize - 1,
      latex === "" ? [] : schema.text(latex),
    );
    this.view.dispatch(tr);
  }

  private render(): void {
    const latex = this.node.textContent;
    this.dom.setAttribute("data-latex", latex);
    this.dom.classList.toggle("wiki-math--editing", this.editing);
    this.source.value = latex;
    this.source.hidden = !this.editing;
    this.rendered.hidden = this.editing;
    if (latex === "") {
      this.rendered.textContent = this.displayMode ? "Empty equation" : "$$";
      this.rendered.classList.add("wiki-math--empty");
      return;
    }
    this.rendered.classList.remove("wiki-math--empty");
    void renderMath(this.rendered, latex, this.displayMode);
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  selectNode(): void {
    if (this.context.readOnly) return;
    this.editing = true;
    this.render();
    this.source.focus();
  }

  deselectNode(): void {
    this.editing = false;
    this.render();
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}

/**
 * A divider and a page break are the same node with different markup, so the
 * node toolbar is the two-item chooser of `editor.md` §5.
 */
export class RuleView implements NodeView {
  readonly dom: HTMLElement;
  private node: ProseMirrorNode;
  private readonly toolbar: HTMLElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("div");
    this.toolbar = document.createElement("div");
    this.toolbar.className = "wiki-node-toolbar";
    this.dom.append(document.createElement("hr"), this.toolbar);
    this.render();
  }

  private setMarkup(markup: string): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    this.view.dispatch(this.view.state.tr.setNodeAttribute(pos, "markup", markup));
  }

  private render(): void {
    const markup = stringAttr(this.node, "markup") ?? "---";
    const pageBreak = markup === PAGE_BREAK_MARKUP;
    this.dom.className = pageBreak ? "wiki-rule wiki-rule--page-break" : "wiki-rule";
    this.dom.setAttribute("data-markup", markup);
    if (pageBreak) this.dom.setAttribute("data-page-break", "true");
    else this.dom.removeAttribute("data-page-break");

    this.toolbar.replaceChildren();
    if (this.context.readOnly) return;
    const divider = toolbarButton("Divider", "Divider", () => {
      this.setMarkup("---");
    });
    const breakButton = toolbarButton("Page break", "Page break", () => {
      this.setMarkup(PAGE_BREAK_MARKUP);
    });
    (pageBreak ? breakButton : divider).setAttribute("aria-pressed", "true");
    this.toolbar.append(divider, breakButton);
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target instanceof HTMLElement && this.toolbar.contains(event.target);
  }

  ignoreMutation(): boolean {
    return true;
  }
}

/** Used by the checkbox node view to place the caret after a click. */
export function selectInside(view: EditorView, pos: number): void {
  view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos + 1))));
}
